import { createFileRoute, Link, notFound, type ErrorComponentProps } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { projects } from "@/lib/portfolio";
import { ProjectSection } from "@/components/work/ProjectSection";
import { publicToInternalSlug, caseOgImages, internalToPublicSlug } from "@/lib/work-data";

function seoDescription(text: string, max = 160) {
  if (text.length <= max) return text;
  const trimmed = text.slice(0, max);
  const lastSpace = trimmed.lastIndexOf(" ");
  return (lastSpace > 0 ? trimmed.slice(0, lastSpace) : trimmed).trimEnd() + "…";
}

export const Route = createFileRoute("/work/$slug")({
  loader: ({ params }) => {
    const internal = publicToInternalSlug[params.slug];
    if (!internal) throw notFound();
    const idx = projects.findIndex((p) => p.slug === internal);
    if (idx === -1) throw notFound();
    return { project: projects[idx], index: idx };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Case study not found | Scott Cunningham" }, { name: "description", content: "This case study could not be found. Explore Scott Cunningham’s portfolio." }, { property: "og:title", content: "Case study not found | Scott Cunningham" }, { property: "og:description", content: "This case study could not be found. Explore Scott Cunningham’s portfolio." }, { property: "og:type", content: "website" }, { name: "twitter:card", content: "summary" }, { name: "robots", content: "noindex" }] };
    }
    const p = loaderData.project;
    const url = `https://scottcbusiness.com/work/${internalToPublicSlug[p.slug]}`;
    const img = caseOgImages[p.slug];
    const title = `${p.name} · Case Study | Scott Cunningham`;
    const desc = seoDescription(p.summary);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:image", content: img },
        { property: "og:image:alt", content: `${p.name} case study preview` },
        { name: "twitter:image:alt", content: `${p.name} case study preview` },
        { property: "og:site_name", content: "Scott Cunningham" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        { name: "twitter:image", content: img },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Article",
                "@id": `${url}#article`,
                headline: `${p.name} · Case Study`,
                description: desc,
                image: img,
                inLanguage: "en",
                mainEntityOfPage: { "@type": "WebPage", "@id": url },
                author: { "@id": "https://scottcbusiness.com/#person" },
                publisher: {
                  "@type": "Organization",
                  "@id": "https://scottcbusiness.com/#organization",
                  name: "Scott Cunningham",
                  url: "https://scottcbusiness.com",
                  logo: {
                    "@type": "ImageObject",
                    url: "https://scottcbusiness.com/og-default.jpg",
                    width: 1200,
                    height: 630,
                  },
                },
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "Home",
                    item: "https://scottcbusiness.com/",
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: "Work",
                    item: "https://scottcbusiness.com/work",
                  },
                  { "@type": "ListItem", position: 3, name: p.name, item: url },
                ],
              },
            ],
          }),
        },
      ],
    };
  },
  component: CaseStudyPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-4xl">Case study not found</h1>
      <p className="mt-4 text-muted-foreground">That case study doesn't exist.</p>
      <Link to="/work" className="mt-6 inline-block text-gradient underline">Back to all work</Link>
    </div>
  ),
  errorComponent: ({ error: rawError, reset }: ErrorComponentProps) => {
    const error = rawError instanceof Error ? rawError : new Error(String(rawError));
    return (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-3xl">Something went wrong</h1>
      <p className="mt-3 text-sm text-muted-foreground">{error.message}</p>
      <button onClick={reset} className="mt-6 rounded-full border-2 border-foreground px-4 py-2 font-mono text-xs uppercase tracking-widest">Try again</button>
    </div>
    );
  },
});

function CaseStudyPage() {
  const { project, index } = Route.useLoaderData();
  return (
    <>
      <ProjectSection
        project={project}
        index={index}
        topSlot={
          <Link
            to="/work"
            className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
            All case studies
          </Link>
        }
      />
      <section className="border-t border-border bg-muted/30">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-6 py-10 lg:px-10">
          <p className="font-display text-xl md:text-2xl">See more work</p>
          <Link
            to="/work"
            className="lift-glow inline-flex items-center gap-2 rounded-full bg-gradient-hero px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-primary-foreground"
          >
            All case studies
          </Link>
        </div>
      </section>
    </>
  );
}
